import React, { useState, useEffect, useRef } from "react";
import Logo from "../layout/Logo";
import { useNavigate } from "react-router-dom";
import {
  validatePassword,
  PASSWORD_MIN_LENGTH,
  PASSWORD_PLACEHOLDER,
  PASSWORD_HELP_TEXT,
} from "../../utils/validation";
import TermsModal from "../generics/TermsOfUse";
import PrivacyPolicy from "../generics/PrivacyPolicy";
import LoginModal from "../generics/LoginModal";
import axiosInstance from "../../utils/AxiosInstance";
import { setPendingVerificationEmail } from "../../utils/authStorage";

function SignUp() {
  const [isTermsModalOpen, setTermsModalOpen] = useState(false);
  const [isPrivacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    password: "",
    acceptTerms: false,
    is18OrOlder: false,
  });

  // server-side error (registration conflict, etc.) shown as a top banner
  const [serverError, setServerError] = useState("");
  // per-field validation errors shown under each input
  const [fieldErrors, setFieldErrors] = useState({});
  const [showResend, setShowResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendNotice, setResendNotice] = useState("");

  const [showTooltip, setShowTooltip] = useState(false);
  const passwordHelpRef = useRef(null);

  // Close the tooltip on Escape or on any click outside of the
  // password-help container. Avoids the previous flash-and-close
  // bug caused by mixing onMouseEnter / onMouseLeave / onClick on
  // the same element.
  useEffect(() => {
    if (!showTooltip) return undefined;
    const handlePointerDown = (e) => {
      if (passwordHelpRef.current && !passwordHelpRef.current.contains(e.target)) {
        setShowTooltip(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setShowTooltip(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKey);
    };
  }, [showTooltip]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const navigate = useNavigate();

  const handleNavigate = (path) => navigate(`/${path}`);

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((s) => ({ ...s, [name]: type === "checkbox" ? checked : value }));
    setFieldErrors((fe) => (fe[name] ? { ...fe, [name]: undefined } : fe));
  };

  const validateForm = () => {
    const errs = {};

    if (!formData.email.trim()) errs.email = "Please enter your email.";
    else if (!EMAIL_RE.test(formData.email.trim())) errs.email = "Please enter a valid email address.";

    if (formData.name.trim() === "") errs.name = "Name is required.";

    // Use the shared password validator — same rules as the backend, so the
    // user gets an actionable message ("Password must include at least one
    // letter and one number") client-side instead of a generic "Request
    // failed with status code 400" bubbling up from the server.
    const passwordError = validatePassword(formData.password);
    if (passwordError) errs.password = passwordError;

    if (!formData.is18OrOlder) errs.is18OrOlder = "You must confirm that you are 18 years or older.";
    if (!formData.acceptTerms) errs.acceptTerms = "You must accept the Terms of Use & Privacy Policy.";

    setFieldErrors(errs);
    setShowResend(false);
    setResendNotice("");
    return Object.keys(errs).length === 0;
  };

  // Map backend/axios errors to friendly text + whether to show resend CTA
  const deriveSignupError = (err) => {
    const status = err && err.response && err.response.status;
    const data = err && err.response && err.response.data;
    const raw =
      typeof data === "string"
        ? data
        : (data && data.message) ||
          (Array.isArray(data && data.parameterViolations)
            ? data.parameterViolations.map((v) => v.message).join("; ")
            : err.message);

    const text = String(raw || "").toLowerCase();

    let message = raw || "Something went wrong. Please try again.";
    let recommendResend = false;

    if (
      text.includes("not yet been activated") ||
      text.includes("not activated") ||
      text.includes("inactive")
    ) {
      message =
        "This email is already registered but not activated. Please check your inbox for the verification code or resend it below.";
      recommendResend = true;
    } else if (text.includes("already registered") || text.includes("already exists") || status === 409) {
      message =
        "This email is already registered. Try logging in, or if you never received the code, resend the activation email below.";
      recommendResend = true;
    }

    return { message, recommendResend };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    setShowResend(false);
    setResendNotice("");

    if (!validateForm()) return;

    try {
      const normalizedEmail = String(formData.email || "").trim().toLowerCase();
      const requestData = {
        name: formData.name,
        email: normalizedEmail,
        password: formData.password,
      };

      await axiosInstance.post("/site/user/new", requestData, {
        headers: { "Content-Type": "application/json" },
      });

      setPendingVerificationEmail(normalizedEmail);

      navigate("/verification", {
        state: { email: normalizedEmail, operation: "verification" },
      });
    } catch (error) {
      const { message, recommendResend } = deriveSignupError(error);
      if (recommendResend) {
        setPendingVerificationEmail(formData.email);
      }
      setServerError(message);
      setShowResend(recommendResend);
    }
  };

  const handleContinueVerification = () => {
    const normalizedEmail = String(formData.email || "").trim().toLowerCase();
    if (!normalizedEmail) return;

    setPendingVerificationEmail(normalizedEmail);
    navigate("/verification", {
      state: { email: normalizedEmail, operation: "verification" },
    });
  };

  const handleResend = async () => {
    if (!formData.email) return;
    setResendLoading(true);
    setResendNotice("");
    try {
      await axiosInstance.post("/site/user/resend-email-code", { email: formData.email });
      setResendNotice("Verification email resent. Please check your inbox (and spam).");
    } catch (err) {
      setResendNotice("Could not resend email right now. Please try again in a moment.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-3 py-4 font-sans"
      style={{ color: "var(--txt)", background: "var(--bg)" }}
    >
      <div
        className="relative rounded-2xl w-full max-w-[min(92vw,480px)] md:max-w-[480px] max-h-[calc(100vh-2rem)] overflow-auto px-6 sm:px-8 py-8"
        style={{
          background: "var(--surf)",
          border: "1px solid var(--border)",
          boxShadow: "var(--shadow-soft)",
        }}
      >
        <button
          onClick={() => handleNavigate("")}
          className="absolute top-4 right-4 text-sm px-3 py-1 rounded-2xl"
          style={{
            background: "var(--surf-2)",
            color: "var(--txt-2)",
            border: "1px solid var(--border)",
          }}
        >
          Home
        </button>

        <div className="flex justify-center mb-6">
          <Logo size="lg" onClick={() => navigate("/")} />
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          {serverError && (
            <div
              className="p-3 rounded-lg space-y-2"
              style={{
                background: "var(--red-dim)",
                border: "1px solid var(--red-border)",
                color: "var(--red)",
              }}
            >
              <p className="text-sm">{serverError}</p>

              {showResend && (
                <div className="flex items-center gap-3 pt-1 flex-wrap">
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendLoading}
                    className="inline-flex items-center rounded-full px-3 py-1.5 text-sm font-semibold disabled:opacity-60"
                    style={{ background: "var(--gold)", color: "#FFFFFF" }}
                  >
                    {resendLoading ? "Resending..." : "Resend verification email"}
                  </button>
                  <button
                    type="button"
                    onClick={handleContinueVerification}
                    className="inline-flex items-center rounded-full px-3 py-1.5 text-sm font-semibold"
                    style={{
                      background: "var(--surf-2)",
                      color: "var(--txt-2)",
                      border: "1px solid var(--border)",
                    }}
                  >
                    Continue verification
                  </button>
                  {resendNotice && (
                    <span className="text-sm" style={{ color: "var(--teal)" }}>{resendNotice}</span>
                  )}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: "var(--txt)" }}>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full p-3 rounded-lg focus:outline-none"
              style={{
                background: "var(--surf-2)",
                color: "var(--txt)",
                border: `1px solid ${fieldErrors.email ? "var(--red-border)" : "var(--border)"}`,
              }}
            />
            {fieldErrors.email && (
              <p className="mt-1.5 text-[11px] flex items-center gap-1" style={{ color: "var(--red)" }}>
                <i className="ti ti-alert-circle" style={{ fontSize: 12 }} /> {fieldErrors.email}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: "var(--txt)" }}>Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full p-3 rounded-lg focus:outline-none"
              style={{
                background: "var(--surf-2)",
                color: "var(--txt)",
                border: `1px solid ${fieldErrors.name ? "var(--red-border)" : "var(--border)"}`,
              }}
            />
            {fieldErrors.name && (
              <p className="mt-1.5 text-[11px] flex items-center gap-1" style={{ color: "var(--red)" }}>
                <i className="ti ti-alert-circle" style={{ fontSize: 12 }} /> {fieldErrors.name}
              </p>
            )}
          </div>

          <div ref={passwordHelpRef}>
            <label className="block text-sm font-medium mb-1 flex items-center" style={{ color: "var(--txt)" }}>
              <span>Password</span>
              <button
                type="button"
                className="ml-2 cursor-pointer inline-flex items-center justify-center w-5 h-5 rounded-full text-[11px] font-bold select-none"
                style={{
                  background: showTooltip ? "var(--teal-dim)" : "var(--surf-3)",
                  color: showTooltip ? "var(--teal)" : "var(--txt-2)",
                  border: `1px solid ${showTooltip ? "var(--teal-border)" : "var(--border)"}`,
                }}
                onClick={(e) => {
                  // Stop the click from bubbling — otherwise our own
                  // document-click listener (below) will see this click,
                  // decide "not inside the popover", and close us right back.
                  e.preventDefault();
                  e.stopPropagation();
                  setShowTooltip((v) => !v);
                }}
                aria-label="Password requirements"
                aria-expanded={showTooltip}
              >
                i
              </button>
            </label>
            {showTooltip && (
              <div
                className="rounded-lg p-2.5 text-[12.5px] mb-2"
                style={{
                  background: "var(--surf-2)",
                  border: "1px solid var(--border)",
                  color: "var(--txt-2)",
                }}
                role="tooltip"
              >
                <p className="font-medium mb-1" style={{ color: "var(--txt)" }}>
                  Password must:
                </p>
                <ul className="list-disc ml-4 space-y-0.5">
                  <li>Be at least {PASSWORD_MIN_LENGTH} characters long</li>
                  <li>Contain at least one letter and one number</li>
                  <li>Not be all the same character (e.g. <code>aaaaaaaa</code>)</li>
                  <li>Not be a simple sequence (e.g. <code>12345678</code>)</li>
                </ul>
              </div>
            )}
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={PASSWORD_PLACEHOLDER}
              aria-describedby="password-help"
              className="w-full p-3 rounded-lg focus:outline-none"
              style={{
                background: "var(--surf-2)",
                color: "var(--txt)",
                border: `1px solid ${fieldErrors.password ? "var(--red-border)" : "var(--border)"}`,
              }}
            />
            {fieldErrors.password && (
              <p className="mt-1.5 text-[11px] flex items-center gap-1" style={{ color: "var(--red)" }}>
                <i className="ti ti-alert-circle" style={{ fontSize: 12 }} /> {fieldErrors.password}
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="is18OrOlder"
                checked={formData.is18OrOlder}
                onChange={handleChange}
                className="rounded"
              />
              <p className="text-sm" style={{ color: "var(--txt-2)" }}>I confirm that I am 18 years or older.</p>
            </div>
            {fieldErrors.is18OrOlder && (
              <p className="mt-1 text-[11px] flex items-center gap-1" style={{ color: "var(--red)" }}>
                <i className="ti ti-alert-circle" style={{ fontSize: 12 }} /> {fieldErrors.is18OrOlder}
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="acceptTerms"
                checked={formData.acceptTerms}
                onChange={handleChange}
                className="rounded"
              />
              <p className="text-sm" style={{ color: "var(--txt-2)" }}>
                I accept the{" "}
                <span
                  onClick={() => setTermsModalOpen(true)}
                  className="cursor-pointer hover:underline"
                  style={{ color: "var(--gold)" }}
                >
                  Terms of Use
                </span>{" "}
                &{" "}
                <span
                  onClick={() => setPrivacyModalOpen(true)}
                  className="cursor-pointer hover:underline"
                  style={{ color: "var(--gold)" }}
                >
                  Privacy Policy
                </span>
              </p>
            </div>
            {fieldErrors.acceptTerms && (
              <p className="mt-1 text-[11px] flex items-center gap-1" style={{ color: "var(--red)" }}>
                <i className="ti ti-alert-circle" style={{ fontSize: 12 }} /> {fieldErrors.acceptTerms}
              </p>
            )}
          </div>

          <button
            type="submit"
            className="w-full font-semibold py-3 rounded-full transition"
            style={{ background: "var(--gold)", color: "#FFFFFF" }}
          >
            CREATE ACCOUNT
          </button>

          <p className="text-center text-sm" style={{ color: "var(--txt-2)" }}>
            Already have an account?{" "}
            <span
              onClick={() => navigate("/login")}
              className="cursor-pointer hover:underline"
              style={{ color: "var(--gold)" }}
            >
              Login
            </span>
          </p>
        </form>
      </div>

      <TermsModal isOpen={isTermsModalOpen} onClose={() => setTermsModalOpen(false)} />
      <PrivacyPolicy isOpen={isPrivacyModalOpen} onClose={() => setPrivacyModalOpen(false)} />
      {isModalOpen && <LoginModal onClose={() => setIsModalOpen(false)} />}
    </div>
  );
}

export default SignUp;
